import {ReactNode, useEffect, useMemo, useRef} from "react";
import {useFormContext, useWatch} from "react-hook-form";
import {css} from "@emotion/react";
import styled from "@emotion/styled";
import classNames from "classnames";

import {calculateRemSize} from "@pg-design/helpers-css";
import {onDesktop} from "@pg-design/helpers-css/";
import {IButtonRadioListOption, Radio} from "@pg-design/radio-module";
import {MaxValueFieldControlled, RadioGroupControlled} from "@pg-design/rhf-components-module";
import {Text} from "@pg-design/text-module";

import {creditContributionDefault} from "../../../credit_calculator/constants/credit_contribution_default";
import {creditPeriodDefault} from "../../../credit_calculator/constants/credit_period_default";
import {calculateInstallment} from "../../../credit_calculator/utils/calculate_installment";
import {calculatePriceFromInstalment} from "../../../credit_calculator/utils/calculate_price_from_instalment";
import {Currency} from "../../../types/Currency";
import {getCurrency} from "../../../utils/get_currency";
import {budgetFilterDefaults} from "../../constants/budget_filter_defaults";
import {IBudgetType} from "../../types/IBudgetType";

import * as cns from "./BudgetField.module.css";

interface IProps {
    radioName: string;
    type: "list" | "switch";
    totalInputName: string;
    monthlyInputName: string;
    totalTitleRender?: () => ReactNode;
    monthlyTitleRender?: () => ReactNode;
    totalMax?: number;
    useBudgetDynamicStep?: boolean;
    currency?: Currency;
    inputVariant?: "row" | "column";
    radioClassName?: string;
    radioGroupClassName?: string;
}

const budgetConfig = {
    total: {
        min: budgetFilterDefaults.totalMin,
        max: budgetFilterDefaults.totalMax,
        step: 5000
    },
    monthly: {
        min: budgetFilterDefaults.monthlyMin,
        max: budgetFilterDefaults.monthlyMax,
        step: 200
    }
};

export function BudgetField(props: IProps) {
    const {radioName, type, totalInputName, totalMax = budgetConfig.total.max, monthlyInputName, totalTitleRender, monthlyTitleRender} = props;

    const budgetType: IBudgetType | undefined = useWatch({name: radioName});
    const totalValue = useWatch({name: totalInputName});
    const monthlyValue = useWatch({name: monthlyInputName});
    const {setValue} = useFormContext();
    const updateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const monthlyMax = Math.round(calculateInstallment(totalMax, creditContributionDefault, creditPeriodDefault) || 0) || budgetConfig.monthly.max;

    useEffect(() => {
        if (!budgetType) {
            return;
        }

        if (budgetType === "total") {
            setValue(monthlyInputName, getNewInstallment(totalValue, monthlyMax), {shouldValidate: true});
        }

        if (budgetType === "monthly") {
            setValue(totalInputName, getNewTotal(monthlyValue, totalMax), {shouldValidate: true});
        }
    }, [budgetType, monthlyValue, totalValue]);

    const currency = getCurrency({currency: props.currency});
    const radioList = useMemo(() => {
        return [
            {
                labelContent: totalTitleRender ? totalTitleRender() : <Text variant="info_txt_1">Kwota maksymalna</Text>,
                value: "total"
            },
            {
                labelContent: monthlyTitleRender ? monthlyTitleRender() : <Text variant="info_txt_1">Rata kredytu</Text>,
                value: "monthly"
            }
        ] as IButtonRadioListOption<IBudgetType>[];
    }, [totalTitleRender, monthlyTitleRender]);

    return (
        <div>
            <RadioGroup
                type={type}
                radioRender={
                    type === "switch"
                        ? undefined
                        : ({name, value, id, isActive, onChange, labelContent, className}) => {
                              return (
                                  <div css={budgetOption}>
                                      <Radio
                                          name={name}
                                          id={id}
                                          value={value}
                                          isActive={isActive}
                                          onChange={(name, isActive, value) => {
                                              if (updateTimeoutRef.current) {
                                                  clearTimeout(updateTimeoutRef.current);
                                              }
                                              onChange(name, isActive, value);
                                          }}
                                          labelContent={labelContent}
                                          className={className}
                                      />
                                      {value === "monthly" ? (
                                          <MaxValueFieldControlled
                                              disabled={value !== budgetType}
                                              key="monthly"
                                              name={monthlyInputName}
                                              min={budgetConfig.monthly.min}
                                              max={monthlyMax}
                                              step={budgetConfig.monthly.step}
                                              rightElement={currency}
                                          />
                                      ) : (
                                          <MaxValueFieldControlled
                                              disabled={value !== budgetType}
                                              key="total"
                                              name={totalInputName}
                                              min={budgetConfig.total.min}
                                              max={totalMax}
                                              step={budgetConfig.total.step}
                                              rightElement={currency}
                                              isDynamicStep={props.useBudgetDynamicStep}
                                          />
                                      )}
                                  </div>
                              );
                          }
                }
                name={radioName}
                options={radioList}
                className={classNames(props.radioGroupClassName)}
                optionClassName={classNames(cns.budgetFieldOption, props.radioClassName)}
            />
            {type === "switch" && (
                <div css={inputsSwitch}>
                    {budgetType === "total" ? (
                        <MaxValueFieldControlled
                            key="total"
                            name={totalInputName}
                            min={budgetConfig.total.min}
                            max={totalMax}
                            step={budgetConfig.total.step}
                            rightElement={currency}
                            isDynamicStep={props.useBudgetDynamicStep}
                            inputVariant={props.inputVariant}
                        />
                    ) : (
                        <MaxValueFieldControlled
                            key="monthly"
                            name={monthlyInputName}
                            min={budgetConfig.monthly.min}
                            max={monthlyMax}
                            step={budgetConfig.monthly.step}
                            rightElement={currency}
                            inputVariant={props.inputVariant}
                        />
                    )}
                </div>
            )}
        </div>
    );
}

// Utils
function getNewInstallment(totalValue: number, monthlyMax: number) {
    const newInstallment = calculateInstallment(totalValue, creditContributionDefault, creditPeriodDefault);

    if (!newInstallment) {
        return monthlyMax;
    }

    // Round to nearest step (200)
    const step = budgetConfig.monthly.step;
    const newInstallmentNumber = Math.round(newInstallment / step) * step;

    if (newInstallmentNumber < budgetConfig.monthly.min) {
        return budgetConfig.monthly.min;
    }

    if (newInstallmentNumber > monthlyMax) {
        return monthlyMax;
    }

    return newInstallmentNumber;
}

function getNewTotal(monthlyValue: number, totalMax: number) {
    const newTotal = calculatePriceFromInstalment(monthlyValue);

    if (!newTotal) {
        return totalMax;
    }

    // Round to nearest step (5000)
    const step = budgetConfig.total.step;
    const newTotalNumber = Math.round(parseInt(newTotal) / step) * step;

    if (newTotalNumber > totalMax) {
        return totalMax;
    }

    if (newTotalNumber < budgetConfig.total.min) {
        return budgetConfig.total.min;
    }

    return newTotalNumber;
}

// Styles
const RadioGroup = styled(RadioGroupControlled)<{type: IProps["type"]}>`
    display: flex;
    gap: ${calculateRemSize(2)};

    ${onDesktop(css`
        gap: ${calculateRemSize(4)};
    `)}

    ${({type}) =>
        type === "list"
            ? css`
                  flex-direction: column;
              `
            : css`
                  margin: 0 0 ${calculateRemSize(2)} 0;
              `}
`;

const budgetOption = css`
    flex: 1;
`;

const inputsSwitch = css`
    justify-content: center;
`;
import {ReactNode, useEffect, useMemo, useRef} from "react";
import {useFormContext, useWatch} from "react-hook-form";
import {css} from "@emotion/react";
import styled from "@emotion/styled";
import classNames from "classnames";

import {calculateRemSize} from "@pg-design/helpers-css";
import {onDesktop} from "@pg-design/helpers-css/";
import {IButtonRadioListOption, Radio} from "@pg-design/radio-module";
import {MaxValueFieldControlled, RadioGroupControlled} from "@pg-design/rhf-components-module";
import {Text} from "@pg-design/text-module";

import {creditContributionDefault} from "../../../credit_calculator/constants/credit_contribution_default";
import {creditPeriodDefault} from "../../../credit_calculator/constants/credit_period_default";
import {calculateInstallment} from "../../../credit_calculator/utils/calculate_installment";
import {calculatePriceFromInstalment} from "../../../credit_calculator/utils/calculate_price_from_instalment";
import {Currency} from "../../../types/Currency";
import {getCurrency} from "../../../utils/get_currency";
import {budgetFilterDefaults} from "../../constants/budget_filter_defaults";
import {IBudgetType} from "../../types/IBudgetType";

import * as cns from "./BudgetField.module.css";

interface IProps {
    radioName: string;
    type: "list" | "switch";
    totalInputName: string;
    monthlyInputName: string;
    totalTitleRender?: () => ReactNode;
    monthlyTitleRender?: () => ReactNode;
    totalMax?: number;
    useBudgetDynamicStep?: boolean;
    currency?: Currency;
    inputVariant?: "row" | "column";
    radioClassName?: string;
    radioGroupClassName?: string;
}

const budgetConfig = {
    total: {
        min: budgetFilterDefaults.totalMin,
        max: budgetFilterDefaults.totalMax,
        step: 5000
    },
    monthly: {
        min: budgetFilterDefaults.monthlyMin,
        max: budgetFilterDefaults.monthlyMax,
        step: 200
    }
};

export function BudgetField(props: IProps) {
    const {radioName, type, totalInputName, totalMax = budgetConfig.total.max, monthlyInputName, totalTitleRender, monthlyTitleRender} = props;

    const budgetType: IBudgetType | undefined = useWatch({name: radioName});
    const totalValue = useWatch({name: totalInputName});
    const monthlyValue = useWatch({name: monthlyInputName});
    const {setValue} = useFormContext();
    const updateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const monthlyMax = Math.round(calculateInstallment(totalMax, creditContributionDefault, creditPeriodDefault) || 0) || budgetConfig.monthly.max;

    useEffect(() => {
        if (!budgetType) {
            return;
        }

        if (budgetType === "total") {
            setValue(monthlyInputName, getNewInstallment(totalValue, monthlyMax), {shouldValidate: true});
        }

        if (budgetType === "monthly") {
            setValue(totalInputName, getNewTotal(monthlyValue, totalMax), {shouldValidate: true});
        }
    }, [budgetType, monthlyValue, totalValue]);

    const currency = getCurrency({currency: props.currency});
    const radioList = useMemo(() => {
        return [
            {
                labelContent: totalTitleRender ? totalTitleRender() : <Text variant="info_txt_1">Kwota maksymalna</Text>,
                value: "total"
            },
            {
                labelContent: monthlyTitleRender ? monthlyTitleRender() : <Text variant="info_txt_1">Rata kredytu</Text>,
                value: "monthly"
            }
        ] as IButtonRadioListOption<IBudgetType>[];
    }, [totalTitleRender, monthlyTitleRender]);

    return (
        <div>
            <RadioGroup
                type={type}
                radioRender={
                    type === "switch"
                        ? undefined
                        : ({name, value, id, isActive, onChange, labelContent, className}) => {
                              return (
                                  <div css={budgetOption}>
                                      <Radio
                                          name={name}
                                          id={id}
                                          value={value}
                                          isActive={isActive}
                                          onChange={(name, isActive, value) => {
                                              if (updateTimeoutRef.current) {
                                                  clearTimeout(updateTimeoutRef.current);
                                              }
                                              onChange(name, isActive, value);
                                          }}
                                          labelContent={labelContent}
                                          className={className}
                                      />
                                      {value === "monthly" ? (
                                          <MaxValueFieldControlled
                                              disabled={value !== budgetType}
                                              key="monthly"
                                              name={monthlyInputName}
                                              min={budgetConfig.monthly.min}
                                              max={monthlyMax}
                                              step={budgetConfig.monthly.step}
                                              rightElement={currency}
                                          />
                                      ) : (
                                          <MaxValueFieldControlled
                                              disabled={value !== budgetType}
                                              key="total"
                                              name={totalInputName}
                                              min={budgetConfig.total.min}
                                              max={totalMax}
                                              step={budgetConfig.total.step}
                                              rightElement={currency}
                                              isDynamicStep={props.useBudgetDynamicStep}
                                          />
                                      )}
                                  </div>
                              );
                          }
                }
                name={radioName}
                options={radioList}
                className={classNames(props.radioGroupClassName)}
                optionClassName={classNames(cns.budgetFieldOption, props.radioClassName)}
            />
            {type === "switch" && (
                <div css={inputsSwitch}>
                    {budgetType === "total" ? (
                        <MaxValueFieldControlled
                            key="total"
                            name={totalInputName}
                            min={budgetConfig.total.min}
                            max={totalMax}
                            step={budgetConfig.total.step}
                            rightElement={currency}
                            isDynamicStep={props.useBudgetDynamicStep}
                            inputVariant={props.inputVariant}
                        />
                    ) : (
                        <MaxValueFieldControlled
                            key="monthly"
                            name={monthlyInputName}
                            min={budgetConfig.monthly.min}
                            max={monthlyMax}
                            step={budgetConfig.monthly.step}
                            rightElement={currency}
                            inputVariant={props.inputVariant}
                        />
                    )}
                </div>
            )}
        </div>
    );
}

// Utils
function getNewInstallment(totalValue: number, monthlyMax: number) {
    const newInstallment = calculateInstallment(totalValue, creditContributionDefault, creditPeriodDefault);

    if (!newInstallment) {
        return monthlyMax;
    }

    // Round to nearest step (200)
    const step = budgetConfig.monthly.step;
    const newInstallmentNumber = Math.round(newInstallment / step) * step;

    if (newInstallmentNumber < budgetConfig.monthly.min) {
        return budgetConfig.monthly.min;
    }

    if (newInstallmentNumber > monthlyMax) {
        return monthlyMax;
    }

    return newInstallmentNumber;
}

function getNewTotal(monthlyValue: number, totalMax: number) {
    const newTotal = calculatePriceFromInstalment(monthlyValue);

    if (!newTotal) {
        return totalMax;
    }

    // Round to nearest step (5000)
    const step = budgetConfig.total.step;
    const newTotalNumber = Math.round(parseInt(newTotal) / step) * step;

    if (newTotalNumber > totalMax) {
        return totalMax;
    }

    if (newTotalNumber < budgetConfig.total.min) {
        return budgetConfig.total.min;
    }

    return newTotalNumber;
}

// Styles
const RadioGroup = styled(RadioGroupControlled)<{type: IProps["type"]}>`
    display: flex;
    gap: ${calculateRemSize(2)};

    ${onDesktop(css`
        gap: ${calculateRemSize(4)};
    `)}

    ${({type}) =>
        type === "list"
            ? css`
                  flex-direction: column;
              `
            : css`
                  margin: 0 0 ${calculateRemSize(2)} 0;
              `}
`;

const budgetOption = css`
    flex: 1;
`;

const inputsSwitch = css`
    justify-content: center;
`;
